import React, { lazy, Suspense, useEffect, useState } from "react";
import { t } from "@/i18n";
import DialogModal from "@/components/dialogModal";
import {
  AlarmClock,
  BellRing,
  Check,
  ChevronLeft,
  ChevronRight,
  Undo2,
  UserCheck2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import ProgressCircle from "../../progressCircle";
import LoadingPanel from "@/components/loadingPanel";
import { cn } from "@/components/utils";
import { useUserProfileCompletion } from "@/hooks/users/useUserProfileCompletion";
import {
  useUserProfileCompletionClearReminder,
  useUserProfileCompletionSetReminder,
  useUserProfileCompletionSkipStage,
  useUserProfileCompletionUnskipStage,
} from "@/hooks/users/useUpdateUserProfileCompletion";
import { Badge } from "@/components/ui/badge";
import UserProfileCompletionIndicator from "./userProfileCompletionIndicator";
const stageComponents = {
  ssnVerified: lazy(() => import("./stages/ssnVerified")),
};

const UserProfileCompletionStageDialog = ({ open, onCancel }) => {
  const [stageId, setStageId] = useState();
  const [openState, setOpenState] = useState(open);

  const { stagesById, data: stages } = useUserProfileCompletion();

  const { mutateAsync: setReminder, isPending: issetReminderPending } =
    useUserProfileCompletionSetReminder(stageId);

  const { mutateAsync: clearReminder, isPending: isClearReminderPending } =
    useUserProfileCompletionClearReminder(stageId);

  const { mutateAsync: skipStage, isPending: isSkipStagePending } =
    useUserProfileCompletionSkipStage(stageId);

  const { mutateAsync: unskipStage, isPending: isUnskipStagePending } =
    useUserProfileCompletionUnskipStage(stageId);

  useEffect(() => {
    setStageId(stages?.[0]?.id);
  }, [stages?.length]);

  useEffect(() => {
    setOpenState(open);
  }, [open]);

  const StageRenderer = stageComponents[stageId];
  const stage = stagesById?.[stageId];
  const nextStage = stagesById?.[stage?.nextStageId];
  const prevStage = stagesById?.[stage?.prevStageId];
  const isCompleted = stage?.isCompleted;
  const shouldRemind = stage?.shouldRemind;
  const skippedAndNotCompleted = stage?.skippedAt && !stage?.completed;
  const showRelevancyButton = !isCompleted || skippedAndNotCompleted;
  const isRemindable = (stage) =>
    stage && !stage.isCompleted && stage.shouldRemind;

  // Auto-open when an incomplete stage is set
  useEffect(() => {
    if (open !== false && stageId && isRemindable(stage)) {
      setOpenState(true);
    }
  }, [open, stageId, isCompleted, shouldRemind]);

  const runAndGoToNext = async (actionFn) => {
    await actionFn();

    const remindableStage = stages.find(
      (s) => isRemindable(s) && s.id !== stageId,
    );

    if (remindableStage) {
      return setStageId(remindableStage.id);
    }
    onCancel();
  };

  const onRemindMeLater = () => runAndGoToNext(setReminder);
  const onSkip = () => runAndGoToNext(skipStage);
  const onUnskip = () => unskipStage(stageId);

  const onNext = () => {
    nextStage && setStageId(nextStage.id);
  };

  const onPrev = () => {
    prevStage && setStageId(prevStage.id);
  };

  const onStageComplete = async () => {
    await clearReminder(); // this also invalidates the userProfileCompletion query

    isRemindable(nextStage) && setTimeout(onNext, 1000);
  };

  const closeButton = (
    <Button variant="outline" className="rounded-full" onClick={onCancel}>
      {t("generic.close")}
    </Button>
  );

  return (
    <DialogModal
      size="lg"
      isLoading={!stage || isClearReminderPending}
      open={!!openState}
      title={
        <h2
          className={cn("flex items-center gap-4", {
            "text-success": isCompleted,
          })}
        >
          {t(`user_profile_completion.stages.${stageId}.title`)}
          {!isCompleted && stage?.nextReminderInDays > 0 && (
            <Badge variant="outline-primary" className="flex gap-1">
              <BellRing size={16} />
              {t("generic.reminder_in_x_days", {
                count: stage.nextReminderInDays,
              })}
            </Badge>
          )}
        </h2>
      }
      className={cn("dialog-sheen", {
        "bg-emerald-50": isCompleted,
      })}
      hideCloseButton
      onCancel={null}
      description={
        !isCompleted && (
          <div className="text-success ml-12 flex items-center gap-2 text-lg font-bold">
            <ProgressCircle
              variant="success"
              radius={10}
              min={0}
              max={100}
              value={stage?.weightPercentage}
              strokeWidth={4}
            />
            +{stage?.weightPercentage}%
          </div>
        )
      }
      icon={
        isCompleted ? (
          <Check strokeWidth={3} className="text-success" />
        ) : (
          <UserCheck2 className="text-slate-500" />
        )
      }
      borderless
      headerRenderer={() => (
        <UserProfileCompletionIndicator className="self-baseline" />
      )}
      footerRenderer={() => (
        <div className="grid w-full grid-cols-[1fr_80px_1fr] px-6">
          <div className="flex items-center gap-2">
            {showRelevancyButton && (
              <Button
                variant="outline"
                className="rounded-full bg-transparent"
                onClick={skippedAndNotCompleted ? onUnskip : onSkip}
                isLoading={isSkipStagePending || isUnskipStagePending}
              >
                {skippedAndNotCompleted ? (
                  <>
                    <Undo2 size={16} className="text-slate-400" />
                    {t("user_profile_completion.relevant_for_me")}
                  </>
                ) : (
                  <>
                    <Check size={16} className="text-slate-400" />
                    {t("user_profile_completion.not_relevant_for_me")}
                  </>
                )}
              </Button>
            )}
          </div>
          {stages.length > 1 ? (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                className="rounded-full bg-transparent"
                onClick={onPrev}
              >
                <ChevronLeft size={14} />
              </Button>
              <Button
                variant="outline"
                className="rounded-full bg-transparent"
                onClick={onNext}
                disabled={!nextStage}
              >
                <ChevronRight size={14} />
              </Button>
            </div>
          ) : (
            <div />
          )}
          <div className="flex items-center justify-end gap-2">
            {isCompleted ? (
              closeButton
            ) : shouldRemind ? (
              <Button
                isLoading={issetReminderPending}
                variant="outline"
                className="rounded-full"
                onClick={onRemindMeLater}
              >
                <AlarmClock size={16} />
                {t("generic.remind_me_x_days", {
                  count: stage?.remindIntervalDays,
                })}
              </Button>
            ) : (
              closeButton
            )}
          </div>
        </div>
      )}
      bodyRenderer={() => (
        <div className="p-12">
          <Suspense fallback={<LoadingPanel />}>
            {StageRenderer ? (
              <StageRenderer
                stage={stage}
                skippedAndNotCompleted={skippedAndNotCompleted}
                onComplete={onStageComplete}
                onClose={onCancel}
              />
            ) : (
              <div>Stage not found</div>
            )}
          </Suspense>
        </div>
      )}
    />
  );
};

export default UserProfileCompletionStageDialog;
